import type { FocusEvent, KeyboardEvent, MutableRefObject } from "react"
import { useCallback, useRef, useState } from "react"

import { nextActiveIndex } from "../model/secure-ref-picker"

type Field = HTMLInputElement | HTMLTextAreaElement

interface Options<T, E extends Field> {
	items: readonly T[]
	/** The field's own ref, when the caller reads a caret off it before a pick. */
	anchorRef?: MutableRefObject<E | null>
	/** Ids the rows and ties the field to the list for a screen reader. */
	listId: string
	onPick: (item: T) => void
	/** Enter with no row highlighted. */
	onEnter?: () => void
	/**
	 * When the list may show. Defaults to "there is a row to show"; a field that
	 * explains an empty list passes its own condition instead.
	 */
	canOpen?: boolean
	/** In a document the arrows move the caret until the list is already showing. */
	arrowsOnlyWhenOpen?: boolean
	pickOnTab?: boolean
	/** A pick is the whole answer, so the list closes and the field takes focus back. */
	closeOnPick?: boolean
}

/**
 * The keyboard and open/close half of a suggestion field. The caller owns the
 * text, what the rows are, and what a pick does with them.
 */
export function useSuggestionCombobox<T, E extends Field = HTMLInputElement>({
	items,
	anchorRef: hostRef,
	listId,
	onPick,
	onEnter,
	canOpen,
	arrowsOnlyWhenOpen,
	pickOnTab,
	closeOnPick,
}: Options<T, E>) {
	const ownRef = useRef<E | null>(null)
	const anchorRef = hostRef ?? ownRef
	const keyboardRef = useRef(false)

	const [focused, setFocused] = useState(false)
	const [dismissed, setDismissed] = useState(false)
	const [activeIndex, setActiveIndex] = useState(-1)
	const [container, setContainer] = useState<HTMLElement | null>(null)

	const open = focused && !dismissed && (canOpen ?? items.length > 0)
	const activeItem = activeIndex >= 0 ? items[activeIndex] : undefined

	const handleFocus = useCallback((event: FocusEvent<E>) => {
		setFocused(true)
		setDismissed(false)
		// Resolved off the field so the list portals inside the sheet rather than
		// behind it.
		setContainer(event.target.closest<HTMLElement>('[role="dialog"]') ?? null)
	}, [])

	const handleBlur = useCallback(() => setFocused(false), [])

	const pick = useCallback(
		(item: T) => {
			onPick(item)
			setActiveIndex(-1)
			if (!closeOnPick) return
			setDismissed(true)
			// The pick came from a pointer on the row, which took focus off the field.
			anchorRef.current?.focus()
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[closeOnPick, onPick],
	)

	/** True when the key drove the list, so the caller leaves the field alone. */
	const handleKeyDown = useCallback(
		(event: KeyboardEvent<E>): boolean => {
			const key = event.key
			if (
				(key === "ArrowDown" || key === "ArrowUp") &&
				(!arrowsOnlyWhenOpen || open)
			) {
				event.preventDefault()
				keyboardRef.current = true
				setDismissed(false)
				setActiveIndex((current) => nextActiveIndex(current, items.length, key))
				return true
			}
			if (key === "Escape" && open) {
				// The sheet closes on Escape too, so keep it from bubbling.
				event.preventDefault()
				event.stopPropagation()
				setDismissed(true)
				return true
			}
			if (key === "Tab" && pickOnTab && activeItem) {
				pick(activeItem)
				return true
			}
			if (key !== "Enter") return false
			if (activeItem) {
				event.preventDefault()
				pick(activeItem)
				return true
			}
			if (!onEnter) return false
			event.preventDefault()
			onEnter()
			return true
		},
		[
			activeItem,
			arrowsOnlyWhenOpen,
			items.length,
			onEnter,
			pick,
			open,
			pickOnTab,
		],
	)

	return {
		open,
		focused,
		activeIndex,
		activeItem,
		container,
		anchorRef,
		keyboardRef,
		pick,
		setActiveIndex,
		setDismissed,
		setFocused,
		handleFocus,
		handleBlur,
		handleKeyDown,
		/** Everything the field needs when the caller adds nothing of its own. */
		anchorProps: {
			role: "combobox" as const,
			"aria-expanded": open,
			"aria-controls": listId,
			"aria-activedescendant":
				activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined,
			onFocus: handleFocus,
			onBlur: handleBlur,
			onKeyDown: handleKeyDown,
		},
	}
}
